import { useState, useEffect } from 'react';
import useNotifications from 'core/notifications/useNotifications';
import { Modal, P, Input, Gap } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { useTemplateEditorDispatch } from '../../context/UseTemplateEditorContext';
import { seedRequiredCompilationContextFields } from '../../utils/compilation-context-helpers';
import { importSnapshot } from '../../utils/queries';
import { OnsiteRandomiseSnapshotMapper } from '../../utils/OnsiteRandomiseSnapshotMapper';

interface IProps {
  snapshotModalOpen: boolean;
  setSnapshotModalOpen(isOpen: boolean);
  isOnsite?: boolean;
}

export const TemplateEditorImportSnapshotModal = ({
  snapshotModalOpen,
  setSnapshotModalOpen,
  isOnsite = false,
}: IProps) => {
  const { t } = useTranslation('trackingLinks');
  const [snapshotId, setSnapshotId] = useState('');
  const { showNotification } = useNotifications();
  const dispatch = useTemplateEditorDispatch();
  const { siteId } = useParams<{ siteId: string }>();

  const getSnapshotQuery = useQuery(['getCompilationContext'], () => importSnapshot({ siteId, snapshotId, t }), {
    enabled: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleClose = () => {
    setSnapshotId('');
    setSnapshotModalOpen(false);
  };

  const handleConfirm = () => {
    getSnapshotQuery.refetch();
    setSnapshotId('');
    setSnapshotModalOpen(false);
  };

  useEffect(() => {
    if (!getSnapshotQuery.isLoading) {
      let loadedCompilationContext: any = getSnapshotQuery?.data ?? null;
      if (loadedCompilationContext !== null) {
        if (isOnsite) {
          [loadedCompilationContext] = new OnsiteRandomiseSnapshotMapper().map([loadedCompilationContext]);
        }
        const seededCompilationContext = seedRequiredCompilationContextFields(loadedCompilationContext);
        dispatch({
          type: 'SET_COMPILATION_CONTEXT',
          payload: {
            compilationContext: seededCompilationContext,
          },
        });
      }
    }

    if (getSnapshotQuery.isError) {
      showNotification(t('importSessionModal.notifications.sessionLoadFailed'), 'error');
    }
  }, [
    getSnapshotQuery.isLoading,
    dispatch,
    getSnapshotQuery.data,
    getSnapshotQuery.isError,
    showNotification,
    t,
    isOnsite,
  ]);

  return (
    <Modal
      heading={t('trackingLinks:importSessionModal.heading')}
      variation="clean"
      isOpen={snapshotModalOpen}
      onCancelButtonClicked={handleClose}
      onCloseButtonClicked={handleClose}
      onOkButtonClicked={handleConfirm}
      okButtonLabel={t('trackingLinks:importSessionModal.confirm')}
      cancelButtonLabel={t('trackingLinks:importSessionModal.cancel')}
    >
      <P>{t('trackingLinks:importSessionModal.description')}</P>
      <Gap top={20} />
      <Input
        ariaLabel={t('trackingLinks:importSessionModal.sessionKey')}
        label={t('trackingLinks:importSessionModal.sessionKey')}
        placeholder={t('trackingLinks:importSessionModal.sessionKeyPlaceholder')}
        value={snapshotId}
        onChange={newSnapshotId => setSnapshotId(newSnapshotId)}
        tooltipHeading={t('trackingLinks:importSessionModal.sessionKeyTooltipHeading')}
        tooltipDescription={t('trackingLinks:importSessionModal.sessionKeyTooltipDescription')}
        data-testid="SnapshotIdInput"
      />
    </Modal>
  );
};
